import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Drawer,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  Divider,
  Button,
  Badge,
  TextField,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  ShoppingCart as ShoppingCartIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  LocalShipping as LocalShippingIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useCart } from '../../contexts/CartContext';
import { getShippingCompanies } from '../../services/storeService';
import { ShippingCompany } from '../../types/store';
// استخدام مسار نسبي للصورة الافتراضية
const placeholderImage = '/assets/images/product-placeholder.svg';

interface CartDrawerProps {
  open: boolean;
  onClose: () => void;
}

const CartDrawer: React.FC<CartDrawerProps> = ({ open, onClose }) => {
  const { cart, removeFromCart, updateQuantity, clearCart } = useCart();
  const [shippingCompanies, setShippingCompanies] = useState<ShippingCompany[]>([]);
  const [estimatedShippingCost, setEstimatedShippingCost] = useState<number>(0);
  const [loadingShipping, setLoadingShipping] = useState<boolean>(false);
  const [shippingError, setShippingError] = useState<string | null>(null);

  // جلب شركات الشحن وحساب متوسط تكلفة الشحن
  useEffect(() => {
    const fetchShippingCost = async () => {
      if (cart.items.length === 0) {
        setEstimatedShippingCost(0);
        return;
      }

      try {
        setLoadingShipping(true);
        setShippingError(null);

        const companies = await getShippingCompanies();
        setShippingCompanies(companies);

        // حساب متوسط تكلفة الشحن من جميع المناطق المتاحة
        if (companies && companies.length > 0) {
          let totalCost = 0;
          let regionCount = 0;

          companies.forEach(company => {
            if (company.regions && company.regions.length > 0) {
              company.regions.forEach(region => {
                if (region.isActive) {
                  totalCost += region.cost;
                  regionCount++;
                }
              });
            }
          });

          // حساب المتوسط إذا وجدت مناطق
          if (regionCount > 0) {
            const averageCost = Math.round(totalCost / regionCount);
            setEstimatedShippingCost(averageCost);
          } else {
            setEstimatedShippingCost(0);
          }
        } else {
          setEstimatedShippingCost(0);
        }
      } catch (err) {
        console.error('Error fetching shipping cost:', err);
        setShippingError('تعذر حساب تكلفة الشحن');
        setEstimatedShippingCost(0);
      } finally {
        setLoadingShipping(false);
      }
    };

    fetchShippingCost();
  }, [cart.items.length]);

  const handleQuantityChange = (productId: string, newQuantity: number) => {
    if (newQuantity >= 1) {
      updateQuantity(productId, newQuantity);
    }
  };

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 400 } }
      }}
    >
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" component="div">
          <Badge badgeContent={cart.totalItems} color="primary" sx={{ mr: 1 }}>
            <ShoppingCartIcon />
          </Badge>
          سلة التسوق
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider />

      {cart.items.length === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            سلة التسوق فارغة
          </Typography>
          <Button
            variant="contained"
            component={RouterLink}
            to="/"
            onClick={onClose}
          >
            تسوق الآن
          </Button>
        </Box>
      ) : (
        <>
          <List sx={{ flexGrow: 1, overflow: 'auto' }}>
            {cart.items.map((item) => (
              <React.Fragment key={item.productId}>
                <ListItem
                  secondaryAction={
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => removeFromCart(item.productId)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  }
                >
                  <ListItemAvatar>
                    <Avatar
                      src={item.product.imageUrl || placeholderImage}
                      alt={item.product.name}
                      variant="rounded"
                      sx={{ width: 60, height: 60, mr: 2 }}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={item.product.name}
                    secondary={
                      <Typography variant="body2" color="text.secondary">
                        {item.product.sellingPrice.toLocaleString('ar-EG')} ج.م
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <Box sx={{ display: 'flex', alignItems: 'center', ml: 9 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleQuantityChange(item.productId, item.quantity - 1)}
                    >
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                    <TextField
                      size="small"
                      value={item.quantity}
                      onChange={(e) => {
                        const value = parseInt(e.target.value);
                        if (!isNaN(value)) {
                          handleQuantityChange(item.productId, value);
                        }
                      }}
                      inputProps={{
                        min: 1,
                        style: { textAlign: 'center' }
                      }}
                      sx={{ width: 60, mx: 1 }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleQuantityChange(item.productId, item.quantity + 1)}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="body1" sx={{ ml: 2, fontWeight: 'bold' }}>
                      {(item.product.sellingPrice * item.quantity).toLocaleString('ar-EG')} ج.م
                    </Typography>
                  </Box>
                </ListItem>
                <Divider variant="inset" component="li" />
              </React.Fragment>
            ))}
          </List>

          <Box sx={{ p: 2 }}>
            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body1">المجموع الفرعي:</Typography>
              <Typography variant="body1">
                {cart.totalAmount.toLocaleString('ar-EG')} ج.م
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LocalShippingIcon fontSize="small" sx={{ mr: 0.5 }} />
                <Typography variant="body1">تكلفة الشحن (تقديرية):</Typography>
                <Tooltip title="هذه تكلفة تقديرية. سيتم تحديد التكلفة النهائية عند إتمام الطلب حسب المنطقة المختارة">
                  <InfoIcon fontSize="small" sx={{ ml: 0.5, color: 'text.secondary' }} />
                </Tooltip>
              </Box>
              {loadingShipping ? (
                <CircularProgress size={16} />
              ) : (
                <Typography variant="body1">
                  {estimatedShippingCost.toLocaleString('ar-EG')} ج.م
                </Typography>
              )}
            </Box>

            {shippingError && (
              <Typography variant="caption" color="error" sx={{ mb: 1, display: 'block' }}>
                {shippingError}
              </Typography>
            )}

            <Divider sx={{ my: 1 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">الإجمالي:</Typography>
              <Typography variant="h6" fontWeight="bold">
                {(cart.totalAmount + estimatedShippingCost).toLocaleString('ar-EG')} ج.م
              </Typography>
            </Box>

            <Button
              variant="contained"
              color="primary"
              fullWidth
              component={RouterLink}
              to="/checkout"
              onClick={onClose}
              sx={{ mb: 1 }}
            >
              إتمام الطلب
            </Button>

            <Button
              variant="outlined"
              color="secondary"
              fullWidth
              onClick={clearCart}
            >
              إفراغ السلة
            </Button>
          </Box>
        </>
      )}
    </Drawer>
  );
};

export default CartDrawer;
